/**
 * INYRON Ω-Spectrum
 *
 * Deterministic nonlinear signal substrate for the Living Universe.
 * It is a simulation abstraction inspired by harmonics, beats, resonance and
 * intermodulation. "Unnamed modes" are emergent simulation modes, not claims
 * of undiscovered physical frequencies or particles.
 */
import type { LifeEntity } from "./universal-life-runtime";

export type SpectrumDomain =
  | "matter" | "life" | "mind" | "combat" | "environment"
  | "audio" | "visual" | "space" | "system" | "custom";

export interface SpectrumVec3 { x: number; y: number; z?: number }

export interface SpectralEmitter {
  id: string;
  domain: SpectrumDomain;
  baseFrequency: number;
  amplitude: number;
  phase: number;
  coherence: number;
  entropy: number;
  nonlinearGain: number;
  harmonics: number;
  position?: SpectrumVec3;
  sourceId?: string;
}

export interface HarmonicProjectionFrame {
  fundamentalHz: number;
  orderCount?: number;
  waveform?: number;
  beatEnvelope?: number;
  intermodulation?: number;
  phaseCoherence?: number;
  spectralCentroid01?: number;
  vfxEmission?: number;
  audioBrightness?: number;
  physicsResponse?: number;
  aiArousal?: number;
}

export interface HarmonicProjectionOptions {
  id: string;
  sourceId?: string;
  domain?: SpectrumDomain;
  position?: SpectrumVec3;
  amplitude?: number;
  phase?: number;
  nonlinearGain?: number;
}

export interface DomainSignalFrame {
  energy01: number;
  coherence01?: number;
  entropy01?: number;
  complexity01?: number;
  tension01?: number;
  novelty01?: number;
  phase01?: number;
  frequencyHz?: number;
  harmonicDetail01?: number;
}

export interface DomainProjectionOptions {
  id: string;
  sourceId?: string;
  domain: SpectrumDomain;
  position?: SpectrumVec3;
}

export interface PhysicalFrequencyFrame {
  frequenciesHz: number[];
  sourceBandHz: { min: number; max: number };
  beatSourceBandHz?: { min: number; max: number };
  carrierBand?: { min: number; max: number };
  beatBand?: { min: number; max: number };
  energy01?: number;
  coherence01?: number;
  entropy01?: number;
  nonlinearGain01?: number;
  phase01?: number;
}

export interface PhysicalFrequencyProjectionResult {
  emitters: SpectralEmitter[];
  sourceFrequenciesHz: number[];
  sourceBeatFrequenciesHz: number[];
  mappedCarrierFrequencies: number[];
  mappedBeatFrequencies: number[];
}

export type SpectralModeFamily =
  | "fundamental" | "harmonic" | "beat" | "sum" | "difference" | "intermodulation" | "cascade";

export interface EmergentSpectralMode {
  id: string;
  family: SpectralModeFamily;
  frequency: number;
  amplitude: number;
  phase: number;
  coherence: number;
  stability: number;
  novelty: number;
  occurrences: number;
  firstSeenAt: number;
  lastSeenAt: number;
  originIds: string[];
  generation: number;
  parentModeIds: string[];
}

export interface SpectralResonance {
  aId: string;
  bId: string;
  score: number;
  beatFrequency: number;
  harmonicA: number;
  harmonicB: number;
  detune: number;
}

export interface OmegaSpectrumSnapshot {
  version: 1;
  emitters: SpectralEmitter[];
  modes: EmergentSpectralMode[];
  emitterCursor?: number;
  pairCursor: number;
  cascadeCursor?: number;
}

export interface OmegaSpectrumStats {
  emitters: number;
  modes: number;
  processedEmitters: number;
  processedPairs: number;
  processedCascades: number;
  discoveries: number;
}

export interface SpectrumEvent {
  type: "spectrum.mode.discovered" | "spectrum.mode.reinforced" | "spectrum.resonance";
  atSeconds: number;
  payload: EmergentSpectralMode | SpectralResonance;
}

export interface OmegaSpectrumOptions {
  nonlinearOrder: number;
  emitterBudget: number;
  pairBudget: number;
  cascadeBudget: number;
  maxCascadeGeneration: number;
  minCascadeStability: number;
  minCascadeOccurrences: number;
  maxModes: number;
  modeTtlSeconds: number;
  minModeAmplitude: number;
  resonanceTolerance: number;
  frequencyResolution: number;
  onEvent?: (event: SpectrumEvent) => void;
}

const TAU = Math.PI * 2;
const finite = (v: unknown, fallback = 0) =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;
const clamp01 = (v: number) => Math.max(0, Math.min(1, finite(v)));
const positive = (v: unknown, fallback: number) =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? v : fallback;
const phaseWrap = (v: number) => {
  const value = finite(v);
  return ((value % TAU) + TAU) % TAU;
};

function hash32(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function cloneEmitter(e: SpectralEmitter): SpectralEmitter {
  return { ...e, ...(e.position ? { position: { ...e.position } } : {}) };
}

function cloneMode(m: EmergentSpectralMode): EmergentSpectralMode {
  return {
    ...m,
    generation: Math.max(0, Math.trunc(finite(m.generation))),
    originIds: [...m.originIds],
    parentModeIds: [...(m.parentModeIds ?? [])],
  };
}

export class OmegaSpectrumEngine {
  private readonly emitters = new Map<string, SpectralEmitter>();
  private readonly modes = new Map<string, EmergentSpectralMode>();
  private emitterCursor = 0;
  private pairCursor = 0;
  private cascadeCursor = 0;
  private discoveriesLastTick = 0;
  private processedEmittersLastTick = 0;
  private processedPairsLastTick = 0;
  private processedCascadesLastTick = 0;
  readonly options: OmegaSpectrumOptions;

  constructor(options: Partial<OmegaSpectrumOptions> = {}) {
    this.options = {
      nonlinearOrder: Math.max(1, Math.min(6, Math.trunc(positive(options.nonlinearOrder, 3)))),
      emitterBudget: Math.max(1, Math.min(4096, Math.trunc(positive(options.emitterBudget, 32)))),
      pairBudget: Math.max(1, Math.trunc(positive(options.pairBudget, 48))),
      cascadeBudget: Math.max(0, Math.min(256, Math.trunc(finite(options.cascadeBudget, 8)))),
      maxCascadeGeneration: Math.max(0, Math.min(6, Math.trunc(finite(options.maxCascadeGeneration, 3)))),
      minCascadeStability: clamp01(finite(options.minCascadeStability, 0.45)),
      minCascadeOccurrences: Math.max(2, Math.min(64, Math.trunc(positive(options.minCascadeOccurrences, 2)))),
      maxModes: Math.max(32, Math.trunc(positive(options.maxModes, 1024))),
      modeTtlSeconds: positive(options.modeTtlSeconds, 180),
      minModeAmplitude: clamp01(finite(options.minModeAmplitude, 0.0125)),
      resonanceTolerance: Math.min(1, positive(options.resonanceTolerance, 0.015)),
      frequencyResolution: positive(options.frequencyResolution, 0.0001),
      onEvent: options.onEvent,
    };
  }

  setEventSink(handler?: (event: SpectrumEvent) => void): void {
    this.options.onEvent = handler;
  }

  register(input: SpectralEmitter): SpectralEmitter {
    const emitter: SpectralEmitter = {
      ...input,
      id: String(input.id),
      baseFrequency: positive(input.baseFrequency, 1),
      amplitude: clamp01(input.amplitude),
      phase: phaseWrap(input.phase),
      coherence: clamp01(input.coherence),
      entropy: clamp01(input.entropy),
      nonlinearGain: clamp01(input.nonlinearGain),
      harmonics: Math.max(1, Math.min(12, Math.trunc(positive(input.harmonics, 1)))),
      ...(input.position ? { position: { ...input.position } } : {}),
    };
    this.emitters.set(emitter.id, emitter);
    return cloneEmitter(emitter);
  }

  unregister(id: string): boolean { return this.emitters.delete(id); }
  emitter(id: string): SpectralEmitter | undefined {
    const value = this.emitters.get(id);
    return value ? cloneEmitter(value) : undefined;
  }

  mode(id: string): EmergentSpectralMode | undefined {
    const value = this.modes.get(id);
    return value ? cloneMode(value) : undefined;
  }

  strongestModes(limit = 16): EmergentSpectralMode[] {
    return [...this.modes.values()]
      .sort((a, b) => (b.amplitude * b.stability) - (a.amplitude * a.stability) || a.id.localeCompare(b.id))
      .slice(0, Math.max(0, limit))
      .map(cloneMode);
  }

  emittersForModes(
    modes: readonly EmergentSpectralMode[],
    limit = 256,
  ): SpectralEmitter[] {
    const ids = new Set<string>();
    const cap = Math.max(0, Math.trunc(limit));
    for (const mode of modes) {
      for (const originId of mode.originIds) {
        if (this.emitters.has(originId)) ids.add(originId);
        if (ids.size >= cap) break;
      }
      if (ids.size >= cap) break;
    }
    return [...ids]
      .sort()
      .slice(0, cap)
      .map(id => this.emitters.get(id))
      .filter((value): value is SpectralEmitter => Boolean(value))
      .map(cloneEmitter);
  }

  lineage(modeId: string, maxDepth = 4): EmergentSpectralMode[] {
    const root = this.modes.get(modeId);
    if (!root) return [];
    const depthLimit = Math.max(0, Math.min(16, Math.trunc(maxDepth)));
    const output: EmergentSpectralMode[] = [];
    const seen = new Set<string>();
    const queue: Array<{ id: string; depth: number }> = [{ id: root.id, depth: 0 }];
    while (queue.length) {
      const current = queue.shift()!;
      if (seen.has(current.id)) continue;
      const mode = this.modes.get(current.id);
      if (!mode) continue;
      seen.add(current.id);
      output.push(cloneMode(mode));
      if (current.depth >= depthLimit) continue;
      for (const parentId of [...(mode.parentModeIds ?? [])].sort()) {
        queue.push({ id: parentId, depth: current.depth + 1 });
      }
    }
    return output;
  }

  resonanceBetween(aId: string, bId: string, maxHarmonic = 6): SpectralResonance | undefined {
    const a = this.emitters.get(aId);
    const b = this.emitters.get(bId);
    if (!a || !b) return undefined;
    let bestDetune = Number.POSITIVE_INFINITY;
    let harmonicA = 1;
    let harmonicB = 1;
    const limit = Math.max(1, Math.min(12, Math.trunc(maxHarmonic)));
    for (let m = 1; m <= limit; m += 1) {
      for (let n = 1; n <= limit; n += 1) {
        const af = a.baseFrequency * m;
        const bf = b.baseFrequency * n;
        const detune = Math.abs(af - bf) / Math.max(af, bf, Number.EPSILON);
        if (detune < bestDetune) { bestDetune = detune; harmonicA = m; harmonicB = n; }
      }
    }
    const closeness = 1 - Math.min(1, bestDetune / this.options.resonanceTolerance);
    const energetic = Math.sqrt(a.amplitude * b.amplitude);
    const coherent = Math.sqrt(a.coherence * b.coherence);
    return {
      aId, bId,
      score: clamp01(closeness * energetic * coherent),
      beatFrequency: Math.abs(a.baseFrequency - b.baseFrequency),
      harmonicA, harmonicB, detune: bestDetune,
    };
  }

  tick(nowSeconds: number, dtSeconds = 1 / 60): OmegaSpectrumStats {
    if (!Number.isFinite(nowSeconds) || !(dtSeconds > 0)) return this.stats();
    this.discoveriesLastTick = 0;
    this.processedEmittersLastTick = 0;
    this.processedPairsLastTick = 0;
    this.processedCascadesLastTick = 0;
    const emitters = [...this.emitters.values()].sort((a, b) => a.id.localeCompare(b.id));

    const emitterBudget = Math.min(this.options.emitterBudget, emitters.length);
    for (let step = 0; step < emitterBudget; step += 1) {
      const emitter = emitters[(this.emitterCursor + step) % emitters.length];
      this.observeMode("fundamental", emitter.baseFrequency, emitter.amplitude, emitter.phase,
        emitter.coherence, [emitter.id], nowSeconds);
      for (let h = 2; h <= emitter.harmonics; h += 1) {
        this.observeMode("harmonic", emitter.baseFrequency * h, emitter.amplitude / h,
          emitter.phase * h, emitter.coherence * (1 - emitter.entropy * 0.25), [emitter.id], nowSeconds);
      }
      this.processedEmittersLastTick += 1;
    }
    if (emitters.length) this.emitterCursor = (this.emitterCursor + emitterBudget) % emitters.length;

    const pairCount = emitters.length > 1 ? (emitters.length * (emitters.length - 1)) / 2 : 0;
    const budget = Math.min(this.options.pairBudget, pairCount);
    for (let step = 0; step < budget; step += 1) {
      const pair = this.pairAt(emitters, (this.pairCursor + step) % pairCount);
      if (!pair) break;
      this.mixPair(pair[0], pair[1], nowSeconds);
      this.processedPairsLastTick += 1;
    }
    if (pairCount) this.pairCursor = (this.pairCursor + budget) % pairCount;

    this.mixStableModes(nowSeconds);

    for (const [id, mode] of this.modes) {
      if (nowSeconds - mode.lastSeenAt > this.options.modeTtlSeconds) this.modes.delete(id);
    }
    if (this.modes.size > this.options.maxModes) {
      const trim = [...this.modes.values()]
        .sort((a, b) => (a.amplitude * a.stability) - (b.amplitude * b.stability) || a.lastSeenAt - b.lastSeenAt);
      const removeCount = this.modes.size - this.options.maxModes;
      for (let i = 0; i < removeCount; i += 1) this.modes.delete(trim[i].id);
    }
    return this.stats();
  }

  snapshot(): OmegaSpectrumSnapshot {
    return {
      version: 1,
      emitters: [...this.emitters.values()].map(cloneEmitter),
      modes: [...this.modes.values()].map(cloneMode),
      emitterCursor: this.emitterCursor,
      pairCursor: this.pairCursor,
      cascadeCursor: this.cascadeCursor,
    };
  }

  restore(snapshot: unknown): { emitters: number; modes: number } {
    if (!snapshot || typeof snapshot !== "object") return { emitters: 0, modes: 0 };
    const candidate = snapshot as Partial<OmegaSpectrumSnapshot>;
    if (candidate.version !== 1) return { emitters: 0, modes: 0 };
    this.emitters.clear();
    this.modes.clear();
    for (const emitter of candidate.emitters ?? []) if (emitter?.id) this.register(emitter);
    for (const mode of candidate.modes ?? []) {
      if (!mode?.id || !Number.isFinite(mode.frequency)) continue;
      this.modes.set(mode.id, cloneMode(mode));
    }
    this.emitterCursor = Math.max(0, Math.trunc(candidate.emitterCursor ?? 0));
    this.pairCursor = Math.max(0, Math.trunc(candidate.pairCursor ?? 0));
    this.cascadeCursor = Math.max(0, Math.trunc(candidate.cascadeCursor ?? 0));
    return { emitters: this.emitters.size, modes: this.modes.size };
  }

  stats(): OmegaSpectrumStats {
    return {
      emitters: this.emitters.size,
      modes: this.modes.size,
      processedEmitters: this.processedEmittersLastTick,
      processedPairs: this.processedPairsLastTick,
      processedCascades: this.processedCascadesLastTick,
      discoveries: this.discoveriesLastTick,
    };
  }

  static downMapFrequency(
    frequency: number,
    source: { min: number; max: number },
    target: { min: number; max: number },
    logarithmic = true,
  ): number {
    const min = positive(source.min, Number.EPSILON);
    const max = Math.max(min + Number.EPSILON, positive(source.max, min + 1));
    const f = Math.max(min, Math.min(max, positive(frequency, min)));
    const t = logarithmic
      ? (Math.log(f) - Math.log(min)) / (Math.log(max) - Math.log(min))
      : (f - min) / (max - min);
    return target.min + clamp01(t) * (target.max - target.min);
  }

  private pairAt<T>(items: T[], pairIndex: number): [T, T] | undefined {
    const count = items.length;
    if (count < 2) return undefined;
    const pairCount = (count * (count - 1)) / 2;
    const cursor = Math.trunc(pairIndex);
    if (cursor < 0 || cursor >= pairCount) return undefined;

    let low = 0;
    let high = count - 2;
    while (low <= high) {
      const row = (low + high) >> 1;
      const rowStart = (row * (2 * count - row - 1)) / 2;
      const nextStart = ((row + 1) * (2 * count - row - 2)) / 2;
      if (cursor < rowStart) {
        high = row - 1;
        continue;
      }
      if (cursor >= nextStart) {
        low = row + 1;
        continue;
      }
      const offset = cursor - rowStart;
      return [items[row], items[row + 1 + offset]];
    }
    return undefined;
  }

  private mixStableModes(nowSeconds: number): void {
    if (this.options.cascadeBudget <= 0 || this.options.maxCascadeGeneration <= 0) return;
    const candidates = [...this.modes.values()]
      .filter(mode =>
        mode.occurrences >= this.options.minCascadeOccurrences &&
        mode.stability >= this.options.minCascadeStability &&
        mode.generation < this.options.maxCascadeGeneration
      )
      .sort((a, b) =>
        (b.amplitude * b.stability) - (a.amplitude * a.stability) ||
        a.id.localeCompare(b.id)
      )
      .slice(0, 96);
    const pairCount = candidates.length > 1 ? (candidates.length * (candidates.length - 1)) / 2 : 0;
    if (!pairCount) return;
    const budget = Math.min(this.options.cascadeBudget, pairCount);

    for (let step = 0; step < budget; step += 1) {
      const pair = this.pairAt(candidates, (this.cascadeCursor + step) % pairCount);
      if (!pair) break;
      const [a, b] = pair;
      const generation = Math.max(a.generation, b.generation) + 1;
      if (generation > this.options.maxCascadeGeneration) continue;
      const coherence = Math.sqrt(a.coherence * b.coherence);
      const stability = Math.sqrt(a.stability * b.stability);
      const amplitude = Math.sqrt(a.amplitude * b.amplitude) * coherence * stability * (0.42 / generation);
      if (amplitude < this.options.minModeAmplitude) continue;

      const origins = [...new Set([...a.originIds, ...b.originIds])].sort().slice(-32);
      const parents = [a.id, b.id].sort();
      const aSideband = Math.abs(2 * a.frequency - b.frequency);
      const bSideband = Math.abs(2 * b.frequency - a.frequency);

      this.observeMode(
        "cascade",
        aSideband,
        amplitude,
        a.phase * 2 - b.phase,
        coherence,
        origins,
        nowSeconds,
        generation,
        parents,
      );
      this.observeMode(
        "cascade",
        bSideband,
        amplitude,
        b.phase * 2 - a.phase,
        coherence,
        origins,
        nowSeconds,
        generation,
        parents,
      );
      this.processedCascadesLastTick += 1;
    }
    this.cascadeCursor = (this.cascadeCursor + budget) % pairCount;
  }

  private mixPair(a: SpectralEmitter, b: SpectralEmitter, nowSeconds: number): void {
    const spatial = this.spatialCoupling(a, b);
    const coherence = Math.sqrt(a.coherence * b.coherence);
    const baseAmplitude = Math.sqrt(a.amplitude * b.amplitude) *
      Math.sqrt(a.nonlinearGain * b.nonlinearGain) * coherence * spatial;
    if (baseAmplitude < this.options.minModeAmplitude) return;

    this.observeMode("beat", Math.abs(a.baseFrequency - b.baseFrequency), baseAmplitude,
      a.phase - b.phase, coherence, [a.id, b.id], nowSeconds);

    for (let m = 1; m <= this.options.nonlinearOrder; m += 1) {
      for (let n = 1; n <= this.options.nonlinearOrder; n += 1) {
        const strength = baseAmplitude / (m * n);
        if (strength < this.options.minModeAmplitude) continue;
        const af = a.baseFrequency * m;
        const bf = b.baseFrequency * n;
        this.observeMode(m === 1 && n === 1 ? "sum" : "intermodulation",
          af + bf, strength, a.phase * m + b.phase * n, coherence, [a.id, b.id], nowSeconds);
        this.observeMode(m === 1 && n === 1 ? "difference" : "intermodulation",
          Math.abs(af - bf), strength, a.phase * m - b.phase * n, coherence, [a.id, b.id], nowSeconds);
      }
    }

    const resonance = this.resonanceBetween(a.id, b.id);
    if (resonance && resonance.score >= 0.5) {
      this.options.onEvent?.({ type: "spectrum.resonance", atSeconds: nowSeconds, payload: resonance });
    }
  }

  private observeMode(
    family: SpectralModeFamily,
    frequency: number,
    amplitude: number,
    phase: number,
    coherence: number,
    origins: string[],
    nowSeconds: number,
    generation = 0,
    parentModeIds: string[] = [],
  ): void {
    if (!Number.isFinite(frequency) || frequency <= 0 || amplitude < this.options.minModeAmplitude) return;
    const quantized = Math.round(frequency / this.options.frequencyResolution) * this.options.frequencyResolution;
    const key = family + ":" + quantized.toFixed(8);
    const id = "Ω-" + hash32(key).toString(36).toUpperCase().padStart(7, "0");
    const current = this.modes.get(id);
    if (!current) {
      const mode: EmergentSpectralMode = {
        id, family, frequency: quantized, amplitude: clamp01(amplitude), phase: phaseWrap(phase),
        coherence: clamp01(coherence), stability: clamp01(coherence * 0.5),
        novelty: 1, occurrences: 1, firstSeenAt: nowSeconds, lastSeenAt: nowSeconds,
        originIds: [...new Set(origins)].sort(),
        generation: Math.max(0, Math.trunc(finite(generation))),
        parentModeIds: [...new Set(parentModeIds)].sort(),
      };
      this.modes.set(id, mode);
      this.discoveriesLastTick += 1;
      this.options.onEvent?.({ type: "spectrum.mode.discovered", atSeconds: nowSeconds, payload: cloneMode(mode) });
      return;
    }
    current.occurrences += 1;
    current.lastSeenAt = nowSeconds;
    current.amplitude = clamp01(current.amplitude * 0.75 + amplitude * 0.25);
    current.coherence = clamp01(current.coherence * 0.8 + coherence * 0.2);
    current.stability = clamp01(current.stability * 0.85 + current.coherence * 0.15);
    current.novelty = 1 / Math.sqrt(current.occurrences);
    current.phase = phaseWrap(current.phase * 0.8 + phaseWrap(phase) * 0.2);
    current.originIds = [...new Set([...current.originIds, ...origins])].sort().slice(-32);
    current.generation = Math.min(
      Math.max(0, Math.trunc(finite(current.generation))),
      Math.max(0, Math.trunc(finite(generation))),
    );
    current.parentModeIds = [...new Set([
      ...(current.parentModeIds ?? []),
      ...parentModeIds,
    ])].sort().slice(-32);
    if ((current.occurrences & (current.occurrences - 1)) === 0) {
      this.options.onEvent?.({ type: "spectrum.mode.reinforced", atSeconds: nowSeconds, payload: cloneMode(current) });
    }
  }

  private spatialCoupling(a: SpectralEmitter, b: SpectralEmitter): number {
    if (!a.position || !b.position) return 1;
    const dx = a.position.x - b.position.x;
    const dy = a.position.y - b.position.y;
    const dz = (a.position.z ?? 0) - (b.position.z ?? 0);
    return 1 / (1 + Math.hypot(dx, dy, dz) * 0.05);
  }
}

export function physicalFrequencyProjection(
  frame: PhysicalFrequencyFrame,
  options: DomainProjectionOptions,
): PhysicalFrequencyProjectionResult {
  const sourceBand = {
    min: positive(frame.sourceBandHz?.min, Number.EPSILON),
    max: positive(frame.sourceBandHz?.max, 1),
  };
  if (sourceBand.max <= sourceBand.min) sourceBand.max = sourceBand.min * 10;

  const carrierBand = {
    min: positive(frame.carrierBand?.min, 0.25),
    max: positive(frame.carrierBand?.max, 32),
  };
  if (carrierBand.max <= carrierBand.min) carrierBand.max = carrierBand.min * 2;

  const beatSourceBand = {
    min: positive(frame.beatSourceBandHz?.min, Number.EPSILON),
    max: positive(frame.beatSourceBandHz?.max, sourceBand.max),
  };
  if (beatSourceBand.max <= beatSourceBand.min) beatSourceBand.max = beatSourceBand.min * 10;

  const beatBand = {
    min: positive(frame.beatBand?.min, 0.05),
    max: positive(frame.beatBand?.max, 8),
  };
  if (beatBand.max <= beatBand.min) beatBand.max = beatBand.min * 2;

  const sourceFrequenciesHz = [...new Set(
    (frame.frequenciesHz ?? [])
      .filter(value => typeof value === "number" && Number.isFinite(value) && value > 0)
      .sort((a, b) => a - b)
      .slice(0, 4),
  )];
  const energy = clamp01(finite(frame.energy01, 0.7));
  const coherence = clamp01(finite(frame.coherence01, 0.8));
  const entropy = clamp01(finite(frame.entropy01, 1 - coherence));
  const nonlinearGain = clamp01(finite(frame.nonlinearGain01, 0.55));
  const seededPhase = frame.phase01 === undefined
    ? (hash32(options.id) % 1_000_003) / 1_000_003
    : clamp01(frame.phase01);

  const emitters: SpectralEmitter[] = [];
  const mappedCarrierFrequencies: number[] = [];
  const mappedBeatFrequencies: number[] = [];
  const sourceBeatFrequenciesHz: number[] = [];
  const carrierAmplitude = sourceFrequenciesHz.length
    ? clamp01(energy / Math.sqrt(sourceFrequenciesHz.length))
    : 0;

  for (let index = 0; index < sourceFrequenciesHz.length; index += 1) {
    const sourceFrequency = sourceFrequenciesHz[index];
    const mapped = OmegaSpectrumEngine.downMapFrequency(
      sourceFrequency,
      sourceBand,
      carrierBand,
      true,
    );
    mappedCarrierFrequencies.push(mapped);
    emitters.push({
      id: `${options.id}:carrier:${index}`,
      sourceId: options.sourceId,
      domain: options.domain,
      baseFrequency: mapped,
      amplitude: carrierAmplitude,
      phase: phaseWrap((seededPhase + index * 0.17320508075688773) * TAU),
      coherence,
      entropy,
      nonlinearGain,
      harmonics: Math.max(1, Math.min(8, 2 + Math.round(coherence * 4))),
      ...(options.position ? { position: { ...options.position } } : {}),
    });
  }

  for (let a = 0; a < sourceFrequenciesHz.length - 1; a += 1) {
    for (let b = a + 1; b < sourceFrequenciesHz.length; b += 1) {
      const sourceBeat = Math.abs(sourceFrequenciesHz[b] - sourceFrequenciesHz[a]);
      if (!(sourceBeat > 0)) continue;
      const mappedBeat = OmegaSpectrumEngine.downMapFrequency(
        sourceBeat,
        beatSourceBand,
        beatBand,
        true,
      );
      sourceBeatFrequenciesHz.push(sourceBeat);
      mappedBeatFrequencies.push(mappedBeat);
      emitters.push({
        id: `${options.id}:physical-beat:${a}:${b}`,
        sourceId: options.sourceId,
        domain: options.domain,
        baseFrequency: mappedBeat,
        amplitude: clamp01(energy * coherence * 0.72),
        phase: phaseWrap((seededPhase + (a + 1) * 0.131 + (b + 1) * 0.197) * TAU),
        coherence,
        entropy: clamp01(entropy * 0.75),
        nonlinearGain: clamp01(Math.max(nonlinearGain, 0.65)),
        harmonics: 3,
        ...(options.position ? { position: { ...options.position } } : {}),
      });
    }
  }

  return {
    emitters,
    sourceFrequenciesHz,
    sourceBeatFrequenciesHz,
    mappedCarrierFrequencies,
    mappedBeatFrequencies,
  };
}

export function domainSignalEmitter(
  frame: DomainSignalFrame,
  options: DomainProjectionOptions,
): SpectralEmitter {
  const seed = hash32(options.id);
  const energy = clamp01(frame.energy01);
  const coherence = clamp01(finite(frame.coherence01, 0.5));
  const entropy = clamp01(finite(frame.entropy01, 1 - coherence));
  const complexity = clamp01(finite(frame.complexity01, 0.35));
  const tension = clamp01(finite(frame.tension01, 0.15));
  const novelty = clamp01(finite(frame.novelty01, 0.2));
  const detail = clamp01(finite(frame.harmonicDetail01, complexity));
  const seededFrequency = 0.45 + (seed % 7550) / 1000;
  const baseFrequency = positive(
    frame.frequencyHz,
    seededFrequency * (0.82 + complexity * 0.28 + tension * 0.12),
  );
  const phase01 = frame.phase01 === undefined
    ? (seed % 1_000_003) / 1_000_003
    : clamp01(frame.phase01);

  return {
    id: options.id,
    sourceId: options.sourceId,
    domain: options.domain,
    baseFrequency,
    amplitude: energy,
    phase: phaseWrap(phase01 * TAU),
    coherence,
    entropy,
    nonlinearGain: clamp01(
      0.05 +
      complexity * 0.42 +
      tension * 0.22 +
      novelty * 0.24 +
      energy * 0.07
    ),
    harmonics: Math.max(1, Math.min(12, 1 + Math.round(detail * 9))),
    ...(options.position ? { position: { ...options.position } } : {}),
  };
}

export function harmonicProjectionEmitter(
  frame: HarmonicProjectionFrame,
  options: HarmonicProjectionOptions,
): SpectralEmitter {
  const coherence = clamp01(finite(frame.phaseCoherence, 0.5));
  const activity = clamp01(Math.max(
    Math.abs(finite(frame.waveform)),
    Math.abs(finite(frame.beatEnvelope)) * 0.7,
    Math.abs(finite(frame.intermodulation)) * 0.9,
    finite(frame.vfxEmission),
    finite(frame.audioBrightness) * 0.7,
    Math.abs(finite(frame.physicsResponse)) * 0.8,
    finite(frame.aiArousal) * 0.6,
  ));
  const orderCount = Math.max(1, Math.trunc(positive(frame.orderCount, 1)));
  return {
    id: options.id,
    sourceId: options.sourceId,
    domain: options.domain ?? "system",
    baseFrequency: positive(frame.fundamentalHz, 1),
    amplitude: clamp01(options.amplitude ?? (0.12 + activity * 0.88)),
    phase: phaseWrap(options.phase ?? (
      finite(frame.waveform) * Math.PI +
      finite(frame.intermodulation) * Math.PI * 0.5
    )),
    coherence,
    entropy: clamp01(1 - coherence),
    nonlinearGain: clamp01(options.nonlinearGain ?? (
      0.16 +
      Math.abs(finite(frame.intermodulation)) * 0.5 +
      Math.abs(finite(frame.beatEnvelope)) * 0.2 +
      finite(frame.spectralCentroid01) * 0.14
    )),
    harmonics: Math.max(1, Math.min(12, Math.round(Math.sqrt(orderCount) * 2))),
    ...(options.position ? { position: { ...options.position } } : {}),
  };
}

export function lifeSpectralEmitter(entity: LifeEntity): SpectralEmitter {
  const speciesSeed = hash32(entity.profile.speciesId);
  const baseFrequency = 0.75 + (speciesSeed % 6000) / 1000;
  const activeEnergy = clamp01(
    0.2 + (1 - entity.drives.fatigue) * 0.22 + entity.drives.purpose * 0.2 +
    entity.drives.curiosity * 0.16 + entity.drives.safety * 0.12,
  );
  const coherence = clamp01(
    entity.profile.cognition * 0.3 + entity.personality.discipline * 0.22 +
    entity.personality.empathy * 0.13 + (1 - entity.personality.impulsivity) * 0.1 +
    entity.drives.purpose * 0.15 + entity.profile.learningRate * 0.1,
  );
  const entropy = clamp01(
    (entity.drives.hunger + entity.drives.thirst + entity.drives.fatigue + (1 - entity.drives.safety)) / 4,
  );
  const harmonicBudget = entity.tier === "heroic" ? 6 : entity.tier === "active" ? 4 : entity.tier === "ambient" ? 3 : 1;
  return {
    id: "life:" + entity.id,
    sourceId: entity.id,
    domain: "life",
    baseFrequency,
    amplitude: entity.alive ? activeEnergy : 0,
    phase: (speciesSeed % 6283) / 1000,
    coherence,
    entropy,
    nonlinearGain: clamp01(0.08 + entity.personality.creativity * 0.42 + entity.profile.learningRate * 0.3),
    harmonics: harmonicBudget,
    position: { ...entity.position },
  };
}
